import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../app/http-common';
import { useCanCreateBrief } from '../hooks/useFrontegg';
import { SAVED_BRIEF_READY_EVENT } from '../features/MyBriefs/events';
import './SavedBriefReadyNotification.css';

const POLL_INTERVAL_MS = 30 * 1000;

function SavedBriefReadyNotification({ enabled }) {
  const navigate = useNavigate();
  const canCreateBrief = useCanCreateBrief();
  const [notification, setNotification] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const sessionStartedAtRef = useRef(new Date().toISOString());
  const lastBroadcastReadyIdRef = useRef('');

  const pollLatest = useCallback(async () => {
    if (!enabled || !canCreateBrief) return;
    try {
      const response = await api.getLatestReadySavedBriefNotification(
        { since: sessionStartedAtRef.current },
        { silent: true },
      );
      const next = response?.item || null;
      if (next?._id) {
        if (lastBroadcastReadyIdRef.current !== next._id) {
          lastBroadcastReadyIdRef.current = next._id;
          window.dispatchEvent(new CustomEvent(SAVED_BRIEF_READY_EVENT, { detail: next }));
        }
        setNotification((prev) => (prev?._id === next._id ? prev : next));
      }
    } catch (_error) {
      // Notification polling should never block the UI.
    }
  }, [enabled, canCreateBrief]);

  useEffect(() => {
    if (!enabled || !canCreateBrief) {
      setNotification(null);
      return undefined;
    }

    sessionStartedAtRef.current = new Date().toISOString();
    pollLatest();
    const intervalId = window.setInterval(pollLatest, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [enabled, canCreateBrief, pollLatest]);

  const handleDismiss = useCallback(async () => {
    if (!notification?._id || loadingAction) return;
    setLoadingAction(true);
    try {
      await api.markSavedBriefNotificationDismissed(notification._id, { silent: true });
    } catch (_error) {
      // Swallow network errors to keep interaction responsive.
    } finally {
      setLoadingAction(false);
      setNotification(null);
    }
  }, [notification, loadingAction]);

  const handleOpen = useCallback(async () => {
    if (!notification?._id || loadingAction) return;
    setLoadingAction(true);
    try {
      await api.markSavedBriefNotificationClicked(notification._id, { silent: true });
    } catch (_error) {
      // Continue navigation even if server tracking fails.
    } finally {
      const targetId = notification._id;
      setLoadingAction(false);
      setNotification(null);
      window.dispatchEvent(new CustomEvent(SAVED_BRIEF_READY_EVENT, {
        detail: {
          ...notification,
          _id: targetId,
          openTarget: true,
        },
      }));
      navigate(`/my-drafts?expand=${encodeURIComponent(targetId)}`, {
        state: {
          savedBriefTargetId: targetId,
          savedBriefFocusNonce: Date.now(),
        },
      });
    }
  }, [notification, loadingAction, navigate]);

  if (!enabled || !canCreateBrief || !notification?._id) {
    return null;
  }

  return (
    <div className="saved-brief-ready-toast">
      <button
        type="button"
        onClick={handleOpen}
        disabled={loadingAction}
        className="saved-brief-ready-toast-link saved-brief-ready-toast-link-no-underline"
      >
        Your draft is ready. Click here to review it.
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        disabled={loadingAction}
        className="saved-brief-ready-toast-dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}

export default SavedBriefReadyNotification;
